import { useState } from 'react';
import { m } from 'framer-motion';
import { Check, Server, Code, Shield } from 'lucide-react';
import { ContactModal } from './ContactModal';

type Tab = 'vps' | 'development' | 'support';

const tariffs = [
  // Серверы (VPS/Dedicated)
  {
    id: 'vps-1',
    category: 'vps',
    name: 'Стартовый',
    icon: <Server className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Процессор', value: '2 Ядра 3.2ГГц' },
      { label: 'ОЗУ', value: '4 GB' },
      { label: 'Диск', value: '60 GB NVMe' },
      { label: 'Локация', value: 'Красноярск / Москва' },
      { label: 'Сеть', value: '1 ГБит/с' }
    ],
    price: 'от 399',
    unit: '₽ / мес',
    popular: false
  },
  {
    id: 'vps-2',
    category: 'vps',
    name: 'Оптимальный',
    icon: <Server className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Процессор', value: '4 Ядра 3.2ГГц' },
      { label: 'ОЗУ', value: '8 GB' },
      { label: 'Диск', value: '120 GB NVMe' },
      { label: 'Локация', value: 'Красноярск / Москва' },
      { label: 'Сеть', value: '1 ГБит/с' }
    ],
    price: 'от 799',
    unit: '₽ / мес',
    popular: true
  },
  {
    id: 'vps-3',
    category: 'vps',
    name: 'Dedicated Pro',
    icon: <Server className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Процессор', value: 'EPYC 3151 4 Ядра' },
      { label: 'ОЗУ', value: '32 GB' },
      { label: 'Диск', value: '1 TB NVMe' },
      { label: 'Локация', value: 'Амстердам' },
      { label: 'Сеть', value: '1 ГБит/с (50ТБ)' }
    ],
    price: 'от 9 899',
    unit: '₽ / мес',
    popular: false
  },

  // Разработка (API/Backend)
  {
    id: 'dev-1',
    category: 'development',
    name: 'API Интеграция',
    icon: <Code className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Формат', value: 'REST / GraphQL' },
      { label: 'Документация', value: 'Swagger / OpenAPI' },
      { label: 'Безопасность', value: 'OAuth 2.0 / JWT' },
      { label: 'Базы данных', value: 'PostgreSQL / Redis' },
      { label: 'Тестирование', value: 'Unit / E2E' }
    ],
    price: 'Индивидуально',
    unit: 'за проект',
    popular: false
  },
  {
    id: 'dev-2',
    category: 'development',
    name: 'Серверная разработка',
    icon: <Code className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Архитектура', value: 'Микросервисы' },
      { label: 'Стек', value: 'Python / Django / Go' },
      { label: 'Оркестрация', value: 'Docker / K8s' },
      { label: 'Нагрузка', value: 'Highload ready' },
      { label: 'CI/CD', value: 'GitLab / GitHub' }
    ],
    price: 'от 2 500',
    unit: '₽ / час',
    popular: true
  },

  // Поддержка (CRM/Сети)
  {
    id: 'sup-1',
    category: 'support',
    name: 'ИТ-Аутсорсинг',
    icon: <Shield className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Обслуживание', value: 'ПК и Серверы' },
      { label: 'Сети', value: 'Монтаж ЛВС / Wi-Fi' },
      { label: 'Оборудование', value: 'MikroTik / Cisco' },
      { label: 'Видеонаблюдение', value: 'IP / Облачное' },
      { label: 'Выезды', value: 'г. Красноярск' }
    ],
    price: 'от 15 000',
    unit: '₽ / мес',
    popular: true
  },
  {
    id: 'sup-2',
    category: 'support',
    name: 'Внедрение CRM',
    icon: <Shield className="w-6 h-6 text-accent" />,
    features: [
      { label: 'Платформы', value: 'Битрикс24 / amoCRM' },
      { label: 'Интеграция', value: 'Телефония / Почта' },
      { label: 'Автоматизация', value: 'Бизнес-процессы' },
      { label: 'Обучение', value: 'Персонал' },
      { label: 'Поддержка', value: 'SLA 24/7' }
    ],
    price: 'от 30 000',
    unit: 'за проект',
    popular: false
  }
];

export const Pricing = () => {
  const [activeTab, setActiveTab] = useState<Tab>('vps');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState('');

  const filteredTariffs = tariffs.filter(t => t.category === activeTab);

  const handleOrder = (tariffName: string) => {
    setSelectedTariff(`Тариф: ${tariffName}`);
    setIsModalOpen(true);
  };

  return (
    <section id="pricing" className="py-24 bg-[#0f172a] relative">
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} subject={selectedTariff} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <m.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-extrabold text-white mb-4"
          >
            Стоимость услуг
          </m.h2>
          <m.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-400 max-w-2xl mx-auto"
          >
            Прозрачное ценообразование на аренду серверов, ИТ-аутсорсинг и серверную разработку.
          </m.p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-[#020617] p-1.5 rounded-xl border border-white/10 overflow-x-auto max-w-full">
            <button
              onClick={() => setActiveTab('vps')}
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'vps' ? 'bg-accent text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              Серверы и Хостинг
            </button>
            <button
              onClick={() => setActiveTab('development')}
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'development' ? 'bg-accent text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              Разработка API
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'support' ? 'bg-accent text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              ИТ-Аутсорсинг и Сети
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
          {filteredTariffs.map((tariff, index) => (
            <m.div
              key={tariff.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`glass-panel rounded-2xl p-8 relative flex flex-col ${tariff.popular ? 'border-accent shadow-[0_0_30px_rgba(59,130,246,0.15)] transform md:-translate-y-2' : 'border-white/10'}`}
            >
              {tariff.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-accent text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  ТОП
                </div>
              )}
              
              <div className="mb-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 border border-blue-500/20">
                  {tariff.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{tariff.name}</h3>
              </div>
              
              <div className="flex items-end gap-2 mb-8">
                <span className="text-4xl font-extrabold text-white">{tariff.price}</span>
                <span className="text-slate-400 font-medium mb-1">{tariff.unit}</span>
              </div>
              
              <div className="space-y-4 flex-grow mb-8">
                {tariff.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-accent shrink-0" />
                    <div>
                      <div className="text-xs text-slate-500 font-medium uppercase">{feature.label}</div>
                      <div className="text-slate-300 text-sm font-semibold">{feature.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={() => handleOrder(tariff.name)}
                className={`w-full py-4 rounded-xl font-bold transition-all duration-300 ${tariff.popular ? 'bg-accent hover:bg-accent-hover text-white glow-effect' : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'}`}
              >
                Оставить заявку
              </button>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};